import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import matter from "gray-matter";
import { Helmet } from "react-helmet";
import Swiper from 'swiper/bundle';
import 'swiper/css/bundle';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const swiperRef = useRef(null);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        // ✅ Load Markdown files using Webpack
        const context = require.context("!!raw-loader!../posts", false, /\.md$/);
        // console.log("📝 Markdown Files Found:", context.keys());

        const markdownFiles = context.keys().map(context);
        const parsedPosts = markdownFiles
        .map((file, index) => {
          if (!file.default || typeof file.default !== "string") {
            console.warn(`⚠️ Skipping invalid markdown file at index ${index}`, file);
            return null;
          }
      
          try {
            const { data, content } = matter(file.default);
            return {
              id: index,
              postId: context.keys()[index].replace("./", "").replace(".md", ""),
              title: data.title || "Untitled Post",
              date: data.date || "1970-01-01",
              content,
            };
          } catch (error) {
            console.error("❌ Failed to parse markdown file:", error);
            return null;
          }
        })
        .filter(Boolean);
      
      // 🟡 Add this: sort newest to oldest
      parsedPosts.sort((a, b) => {
        const dateA = new Date(a.date || "1970-01-01");
        const dateB = new Date(b.date || "1970-01-01");
        return dateB.getTime() - dateA.getTime();
      });
      
      setPosts(parsedPosts);
      } catch (error) {
        console.error("❌ Error loading markdown files:", error);
      }
    };

    loadPosts();
  }, []);

  useEffect(() => {
    if (posts.length > 0 && swiperRef.current && !swiperRef.current.swiper) {
      new Swiper(swiperRef.current, {
        direction: 'vertical',
        slidesPerView: 5,
        centeredSlides: true,
        mousewheel: {
          enabled: true,
          forceToAxis: true,
          releaseOnEdges: true,
          sensitivity: 1,
          invert: false,
          thresholdDelta: 10,
          thresholdTime: 300
        },
        slideToClickedSlide: true,
        touchRatio: 0.2,
        resistanceRatio: 0
      });
    }
  }, [posts]);

  return (
    <>
      <Helmet>
        <title>Blog - limkc.com</title>
        <meta name="description" content="limkc blog." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      
      {/* 
        Following top-down UI architecture:
        - w-full h-full ensures it fills available space
        - overflow-auto handles content overflow without breaking layout
        - py-6 adds vertical padding within the component's bounds
      */}
      <div className="w-full h-full overflow-hidden flex justify-center">
        <div
          className="py-6 min-h-screen w-full"
          style={{
            paddingTop: 'calc(var(--navbar-height) + 1.5rem)',
            backgroundColor: 'transparent',
            color: 'var(--text-primary)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            maxWidth: '1200px',
            paddingLeft: '1rem',
            paddingRight: '1rem'
          }}
        >
          <div ref={swiperRef} className="swiper" style={{ width: '100%', height: '500px' }}>
            <div className="swiper-wrapper">
              {posts.map((post) => (
                <div key={post.id} className="swiper-slide">
                  <Link
                    to={`/blog/${post.postId}`}
                    style={{ color: 'var(--text-primary)', textDecoration: 'none', textAlign: 'center' }}
                    className="hover:underline"
                  >
                    {post.title}
                  </Link>
                  <p style={{ color: '#9CA3AF', marginTop: '0.5rem', fontSize: '0.875rem' }}>{post.date}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .swiper-slide {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
          opacity: 0.2;
          filter: blur(5px);
          transition: all 0.3s ease;
        }
        .swiper-slide-active {
          font-size: 3rem !important;
          font-weight: bold !important;
          opacity: 1 !important;
          filter: blur(0px) !important;
        }
      `}</style>
    </>
  );
};

export default Blog;